import { useEffect } from 'react';
import type { IDeviceService } from '@/services/device/IDeviceService';
import { auditLogService } from '@/services/api/auditLogService';
import { useAuthStore } from '@/store';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useHarnessStore } from '@/store/useHarnessStore';
import { CRC_FALLBACK } from '@/services/device/constants';
import logger from '@/lib/logger';

/**
 * App-wide bridge from device-communication incidents to the generic AuditLog (backend
 * `POST /api/v1/audit-log`). Mounted once at the app root (regardless of which feature page is
 * active) so an incident is durably logged even outside an active harness Batch — unlike the
 * harness Communication Log, which only persists while a Batch is open.
 *
 * Reports CRC32 mismatches and transferIn failure episodes (started/recovered/failed); other
 * device-communication incidents can extend the same subscriptions later without a new
 * mechanism.
 *
 * Best-effort: a failed write here must never disrupt device communication or measurement flow,
 * so failures are caught and logged locally, not surfaced to the user.
 */
export function useDeviceAuditLogging(deviceService: IDeviceService): void {
  useEffect(() => {
    const unsubscribeReceived = deviceService.onReceivedMessage?.((raw, _msg, crcValid) => {
      if (crcValid) return;

      // Skip entirely when not authenticated — the endpoint requires auth, and there is no
      // point generating a guaranteed-401 network request (e.g. before login).
      if (!useAuthStore.getState().isAuthenticated) return;

      const deviceUid = useDeviceStore.getState().deviceInfo?.uid ?? null;
      const batchId = useHarnessStore.getState().activeBatch?.id ?? null;

      auditLogService
        .log({
          eventType: 'device.crc_mismatch',
          severity: 'Warning',
          message: `CRC32 mismatch on a ${raw.length}-byte frame received from the device`,
          details: {
            byteLength: raw.length,
            fallbackUsed: CRC_FALLBACK.ALLOW_DECODE_ON_MISMATCH,
          },
          batchId,
          deviceUid,
        })
        .catch((err) => {
          logger.warn('[useDeviceAuditLogging] Failed to record CRC-mismatch audit-log entry:', err);
        });
    });

    const unsubscribeTransfer = deviceService.onTransferIncident?.((incident) => {
      // Skip entirely when not authenticated — the endpoint requires auth, and there is no
      // point generating a guaranteed-401 network request (e.g. before login).
      if (!useAuthStore.getState().isAuthenticated) return;

      const deviceUid = useDeviceStore.getState().deviceInfo?.uid ?? null;
      const batchId = useHarnessStore.getState().activeBatch?.id ?? null;

      const eventType: Record<typeof incident.phase, string> = {
        started: 'device.usb_transfer_error',
        recovered: 'device.usb_transfer_recovered',
        failed: 'device.usb_transfer_failure',
      };
      const severity: Record<typeof incident.phase, 'Info' | 'Warning' | 'Error'> = {
        started: 'Warning',
        recovered: 'Info',
        failed: 'Error',
      };
      const message: Record<typeof incident.phase, string> = {
        started: `USB transfer error: ${incident.errorMessage}`,
        recovered: `USB transfer recovered after ${incident.consecutiveErrors} failed attempt(s) over ${incident.durationMs}ms`,
        failed: `USB transfer failed ${incident.consecutiveErrors} consecutive times over ${incident.durationMs}ms — connection treated as lost`,
      };

      auditLogService
        .log({
          eventType: eventType[incident.phase],
          severity: severity[incident.phase],
          message: message[incident.phase],
          details: incident,
          batchId,
          deviceUid,
        })
        .catch((err) => {
          logger.warn('[useDeviceAuditLogging] Failed to record transfer-incident audit-log entry:', err);
        });
    });

    return () => {
      unsubscribeReceived?.();
      unsubscribeTransfer?.();
    };
  }, [deviceService]);
}
